import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Send, CornerDownRight, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import Avatar from "./Avatar";
import Reactions from "./Reactions";
import useHashTarget from "../lib/useHashTarget";

/**
 * The one comment thread in the product. Posts and shared positions both use it.
 *
 * There were two implementations — `PostDetail` rendered comments inline, and
 * `InvestorDetail` had its own `Comments` — which had already drifted: one showed an
 * avatar and the other didn't, one showed the investor's reply and the other didn't.
 * Threading them separately would have doubled the work and guaranteed more drift, so
 * they were folded together first.
 *
 * Two levels, and replies are collapsed
 * -------------------------------------
 * A reply belongs to a comment, and nothing nests under a reply — the server enforces
 * this by re-parenting anything deeper. Replies start collapsed behind a count: on a
 * phone, an expanded thread pushes the next comment off the screen, so a reader
 * scrolling for the discussion has to scroll past every answer to it.
 *
 * A deep link overrides that. Following a notification to a collapsed reply would
 * otherwise land on a page where the thing you were sent to see is behind a button.
 */
function timeOf(c) { return c.created_at || ""; }

function Comment({ c, canComment, onReply, replying, setReplying, depth, t, idPrefix }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      await onReply(body, c.id);
      setBody("");
      setReplying(null);
    } finally { setBusy(false); }
  };

  return (
    <div id={`comment-${c.id}`} data-testid={`${idPrefix}-${c.id}`} className="text-sm flex gap-2.5">
      <div className="shrink-0 pt-0.5">
        <Avatar name={c.author_name} src={c.author_avatar} size={depth ? 24 : 30} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-700">{c.author_name}</span>
          {c.status === "pending" && (
            <span className="pill bg-amber-50 text-amber-700 text-[10px]">{t("investors.awaitingReview")}</span>
          )}
        </div>
        <p className="text-slate-600 mt-0.5 whitespace-pre-line break-words">{c.body}</p>

        <div className="flex items-center gap-3 mt-1.5">
          <Reactions targetType="comment" targetId={c.id} initial={c.reactions} size="sm" />
          {canComment && depth === 0 && (
            <button
              onClick={() => setReplying(replying === c.id ? null : c.id)}
              data-testid={`reply-${c.id}`}
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-brand-600"
            >
              <CornerDownRight size={12} /> {t("comments.reply")}
            </button>
          )}
        </div>

        {/* The investor's moderation reply. Distinct from a threaded reply: it is
            attached to the comment record itself and written from the studio queue. */}
        {c.reply && (
          <div className="mt-2 ml-1 pl-3 border-l-2 border-brand-200">
            <div className="text-[11px] font-medium text-brand-600">{t("investors.investorReply")}</div>
            <p className="text-slate-600">{c.reply}</p>
          </div>
        )}

        {replying === c.id && (
          <div className="flex gap-2 mt-2">
            <input
              className="input py-1.5 text-sm"
              autoFocus
              data-testid={`reply-input-${c.id}`}
              placeholder={t("comments.replyTo", { name: c.author_name })}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
            />
            <button className="btn-primary px-3" onClick={send} disabled={busy}
                    data-testid={`reply-send-${c.id}`}>
              <Send size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CommentThread({ comments = [], canComment = false, onAdd,
                                        idPrefix = "comment", emptyText, className = "" }) {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const [expanded, setExpanded] = useState({});
  const [replying, setReplying] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useHashTarget(comments.length > 0);

  // Which root holds the comment a link points at — so it can be opened before the
  // scroll goes looking for an element that is not rendered yet.
  const rootOfHash = useMemo(() => {
    const id = (hash || "").replace("#comment-", "");
    if (!id) return null;
    const root = comments.find((c) => c.id === id || (c.replies || []).some((r) => r.id === id));
    return root ? root.id : null;
  }, [hash, comments]);

  useEffect(() => {
    if (rootOfHash) setExpanded((e) => ({ ...e, [rootOfHash]: true }));
  }, [rootOfHash]);

  const submit = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      await onAdd(body, null);
      setBody("");
    } finally { setBusy(false); }
  };

  const reply = async (text, parentId) => {
    await onAdd(text, parentId);
    setExpanded((e) => ({ ...e, [parentId]: true }));
  };

  const roots = [...comments].sort((a, b) => (timeOf(b) > timeOf(a) ? 1 : -1));

  return (
    <div className={`space-y-4 ${className}`} data-testid={`${idPrefix}-thread`}>
      {roots.map((c) => {
        const replies = c.replies || [];
        const open = !!expanded[c.id];
        return (
          <div key={c.id} className="space-y-2">
            <Comment c={c} canComment={canComment} onReply={reply} replying={replying}
                     setReplying={setReplying} depth={0} t={t} idPrefix={idPrefix} />
            {replies.length > 0 && (
              <div className="ml-9 pl-3 border-l border-slate-100 space-y-3">
                <button
                  onClick={() => setExpanded((e) => ({ ...e, [c.id]: !open }))}
                  data-testid={`toggle-replies-${c.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-brand-600"
                >
                  <MessageSquare size={12} />
                  {open ? t("comments.hideReplies")
                        : t("comments.showReplies", { count: replies.length })}
                </button>
                {open && replies.map((r) => (
                  <Comment key={r.id} c={r} canComment={canComment} onReply={reply}
                           replying={replying} setReplying={setReplying} depth={1} t={t}
                           idPrefix={idPrefix} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {roots.length === 0 && (
        <p className="text-sm text-slate-400">{emptyText || t("posts.noComments")}</p>
      )}

      {canComment && (
        <div className="flex gap-2 pt-1">
          <input
            className="input py-2 text-sm"
            data-testid={`${idPrefix}-input`}
            placeholder={t("investors.commentPlaceholder")}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <button className="btn-primary px-3" onClick={submit} disabled={busy}
                  data-testid={`${idPrefix}-send`}>
            <Send size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
